/**
 * 高级配置类操作的短信验证码本地缓存。
 *
 * 背景：验证码由 AuthVerifyDialog 校验通过后写入 localStorage，供后续高风险操作
 * （无功工作模式、箱变容量变比、策略下发等）在有效期内免重复校验。
 * 该缓存被 advancedConfig / EblockSetting / StrategyController 等多个页面共用，
 * 因此统一收敛到本模块，避免各页面各写一套键名与过期规则。
 *
 * 有效期策略：以「验证码写入时间」为起点，TTL 内视为可用。
 * 前端只做本地过期清理，真正的合法性仍以后端校验为准 ——
 * 本模块的作用是避免在页面初始化等时机做无意义的远程校验并误报「验证码无效」。
 *
 * 归属策略：验证码额外记录其所属手机号。后端校验的是 (手机号, 验证码) 这一对，
 * 手机号才是该验证码的主人。不同电站可能绑定不同的管理员手机号，
 * 若不区分，切换电站后会拿着上一个电站的验证码去校验当前电站的手机号，
 * 导致「没重新获取验证码却提示校验失败」。
 * 按手机号而非 stationId 归属：多个电站可能共用同一手机号，
 * 按电站隔离会让这些站之间切换时被迫重新获取验证码。
 */

const CODE_KEY = 'ConfigSmsCode'
const TIME_KEY = 'advancedConfigAuthTime'
const PHONE_KEY = 'advancedConfigAuthPhone'

/** 验证码本地有效期：5 分钟 */
export const SMS_CODE_TTL = 5 * 60 * 1000

/**
 * 写入验证码，记录写入时间（过期计时起点）与所属手机号
 * @param {string} phone 该验证码对应的手机号
 * @param {string} code 验证码
 */
export function saveSmsCode(phone, code) {
  localStorage.setItem(CODE_KEY, code ?? '')
  localStorage.setItem(TIME_KEY, Date.now().toString())
  localStorage.setItem(PHONE_KEY, phone ?? '')
}

/** 清除验证码及其时间戳、归属手机号 */
export function clearSmsCode() {
  localStorage.removeItem(CODE_KEY)
  localStorage.removeItem(TIME_KEY)
  localStorage.removeItem(PHONE_KEY)
}

/**
 * 是否已超过本地有效期。
 * 无时间戳时视为已过期：可能是旧版本遗留的、或被外部直接写入的验证码，
 * 无法判断新鲜度，按过期处理更安全。
 */
export function isSmsCodeExpired() {
  const code = localStorage.getItem(CODE_KEY)
  if (!code) return true

  const t = Number(localStorage.getItem(TIME_KEY))
  if (!t || Number.isNaN(t)) return true

  return Date.now() - t >= SMS_CODE_TTL
}

/**
 * 本地缓存的验证码是否属于指定手机号。
 * 无归属记录时返回 false：旧版本写入的验证码没有该字段，
 * 无法确认归属，按不匹配处理（升级后首次需重新获取验证码）。
 * @param {string} phone
 */
export function isSmsCodeOwnedBy(phone) {
  if (!phone) return false
  const saved = localStorage.getItem(PHONE_KEY)
  if (!saved) return false
  return String(saved) === String(phone)
}

/**
 * 读取指定手机号的验证码，顺带做惰性过期清理。
 * 已过期则清除并返回空串；归属手机号不匹配则返回空串（但不清除，
 * 因为该验证码对它自己的手机号可能仍然有效，切回去还能复用）。
 * 调用方拿到空串即应触发验证码弹窗。
 * @param {string} phone 当前电站绑定的手机号
 */
export function getSavedSmsCode(phone) {
  if (isSmsCodeExpired()) {
    clearSmsCode()
    return ''
  }
  if (!isSmsCodeOwnedBy(phone)) {
    return ''
  }
  return localStorage.getItem(CODE_KEY) || ''
}

/**
 * 指定手机号是否存在未过期且归属匹配的验证码。
 * 供页面初始化时使用：只判断有无，不发起后端校验，
 * 避免「什么都没操作却提示验证码无效」。
 * @param {string} phone
 */
export function hasValidSmsCode(phone) {
  return !isSmsCodeExpired() && isSmsCodeOwnedBy(phone)
}

/** 距离过期的剩余毫秒数，已过期返回 0 */
export function getSmsCodeRemainingMs() {
  if (isSmsCodeExpired()) return 0
  const t = Number(localStorage.getItem(TIME_KEY))
  return Math.max(0, SMS_CODE_TTL - (Date.now() - t))
}
